import React from "react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, useSortable,
  horizontalListSortingStrategy, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, GripHorizontal, EyeOff } from "lucide-react";

/**
 * MisSortableTable
 * ─────────────────
 * Drag-to-reorder rows AND columns for MIS Pack. Reads:
 *   - `rows`          : [{ key, label, tone, values }]
 *   - `columns`       : [{ id, label|name, code }]
 *   - `rowOrder`      : [row.key]  (state, controlled)
 *   - `columnOrder`   : [column.id]
 *   - `hiddenRows`    : Set of row.key
 *   - `hiddenColumns` : Set of column.id
 * Calls back with the reordered arrays via `onRowOrderChange` and
 * `onColumnOrderChange`. `renderCell(row, col)` returns the cell body.
 */

function SortableColHeader({ id, label, code, onHide }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <th ref={setNodeRef} style={style}
        className="text-right px-2 py-2 select-none whitespace-nowrap"
        data-testid={`mis-col-${id}`}>
      <div className="flex items-center justify-end gap-1">
        <button {...attributes} {...listeners}
                className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-accent"
                data-testid={`mis-col-drag-${id}`}>
          <GripHorizontal className="w-3 h-3" />
        </button>
        <span className="uppercase text-[10px] font-mono">{label}</span>
        {onHide && (
          <button onClick={onHide} title="Hide column"
                    className="text-muted-foreground hover:text-red-500 ml-0.5"
                    data-testid={`mis-col-hide-${id}`}>
            <EyeOff className="w-3 h-3" />
          </button>
        )}
      </div>
    </th>
  );
}

function SortableRow({ id, tone, children, onHide }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    background: isDragging ? "hsl(var(--accent) / 0.05)" : undefined,
  };
  return (
    <tr ref={setNodeRef} style={style}
        className={tone === "subtotal" ? "bg-blue-50 font-semibold text-blue-900" : "hover:bg-accent/5"}
        data-testid={`mis-row-${id}`}>
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-1.5">
          <button {...attributes} {...listeners}
                    className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-accent"
                    data-testid={`mis-row-drag-${id}`}>
            <GripVertical className="w-3 h-3" />
          </button>
          <span>{children[0]}</span>
          {onHide && (
            <button onClick={onHide} title="Hide row"
                      className="text-muted-foreground hover:text-red-500 opacity-0 group-hover:opacity-100 ml-0.5"
                      data-testid={`mis-row-hide-${id}`}>
              <EyeOff className="w-3 h-3" />
            </button>
          )}
        </div>
      </td>
      {children.slice(1)}
    </tr>
  );
}

export default function MisSortableTable({
  rows, columns, rowOrder, columnOrder,
  hiddenRows = new Set(), hiddenColumns = new Set(),
  onRowOrderChange, onColumnOrderChange,
  onHideRow, onHideColumn, renderCell,
}) {
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: { distance: 4 },
  }));

  const orderedRows = (rowOrder && rowOrder.length
    ? rowOrder.map(k => rows.find(r => r.key === k)).filter(Boolean)
    : rows).filter(r => !hiddenRows.has(r.key));
  const orderedCols = (columnOrder && columnOrder.length
    ? columnOrder.map(id => columns.find(c => c.id === id)).filter(Boolean)
    : columns).filter(c => !hiddenColumns.has(c.id));

  const handleColDrag = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = orderedCols.map(c => c.id);
    const oldIx = ids.indexOf(active.id);
    const newIx = ids.indexOf(over.id);
    onColumnOrderChange && onColumnOrderChange(arrayMove(ids, oldIx, newIx));
  };
  const handleRowDrag = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const keys = orderedRows.map(r => r.key);
    const oldIx = keys.indexOf(active.id);
    const newIx = keys.indexOf(over.id);
    onRowOrderChange && onRowOrderChange(arrayMove(keys, oldIx, newIx));
  };

  return (
    <div className="card-sharp overflow-auto">
      <table className="w-full dense">
        <DndContext sensors={sensors} collisionDetection={closestCenter}
                    onDragEnd={handleColDrag}>
          <SortableContext items={orderedCols.map(c => c.id)}
                            strategy={horizontalListSortingStrategy}>
            <thead>
              <tr>
                <th className="text-left px-2 py-2 uppercase text-[10px] font-mono">Line item</th>
                {orderedCols.map(c => (
                  <SortableColHeader key={c.id} id={c.id}
                                     label={c.label || c.name || c.id}
                                     code={c.code}
                                     onHide={onHideColumn ? () => onHideColumn(c.id) : undefined} />
                ))}
              </tr>
            </thead>
          </SortableContext>
        </DndContext>
        <DndContext sensors={sensors} collisionDetection={closestCenter}
                    onDragEnd={handleRowDrag}>
          <SortableContext items={orderedRows.map(r => r.key)}
                            strategy={verticalListSortingStrategy}>
            <tbody>
              {orderedRows.map(r => (
                <SortableRow key={r.key} id={r.key} tone={r.tone}
                              onHide={onHideRow ? () => onHideRow(r.key) : undefined}>
                  {[r.label, ...orderedCols.map(c => (
                    <td key={c.id} className="text-right px-2 py-1.5 tabular-nums">
                      {renderCell(r, c)}
                    </td>
                  ))]}
                </SortableRow>
              ))}
            </tbody>
          </SortableContext>
        </DndContext>
      </table>
    </div>
  );
}
